/**
 * Auth launch / logout policy for dashboard and left-rail services (ARCH-011).
 * Path-relative policy is authored in infrastructure/platform-routes.json
 * and emitted into redirect-registry.json — do not maintain a parallel SoT here.
 *
 * End-user credentials: Keycloak realm patriotic-projects (theme patriotic).
 * Shared Mode C surface: portal service-login (ARCH-012) via registry launch paths.
 */
import type { PublicIntegrationSnapshot } from '../context/publicIntegrationTypes';
import { defaultPublicIntegration } from '../context/publicIntegrationTypes';
import generatedRegistry from '../../generated/redirect-registry.json';
import { resolveServicePath, resolveServiceUrl, tryResolveServicePath } from './serviceUrlRegistry';

export type LaunchMode = 'sso_passthrough' | 'interactive';
export type LogoutMode = 'shared_fallback' | 'service_local';

export interface ServiceAuthPolicy {
  id: string;
  title: string;
  launchMode: LaunchMode;
  logoutMode: LogoutMode;
  supportsSilentSso: boolean;
  /** Portal path that applies policy then redirects to entry. */
  launchPath: string;
  /** Shared fallback after logout / when switch=1. */
  fallbackLoginPath: string;
  /** Absolute OIDC / SSO entry URL for the service. */
  resolveEntryUrl: (origin: string, integration?: PublicIntegrationSnapshot) => string | null;
}

interface GeneratedAuth {
  launchMode?: string;
  logoutMode?: string;
  supportsSilentSso?: boolean;
  launchPath?: string;
  fallbackLoginPath?: string;
  ssoEntryPath?: string;
  nativeLoginPaths?: string[];
}

interface GeneratedRedirectItem {
  service: string;
  id?: string;
  entryPath?: string;
  ssoEntryPath?: string;
  paths?: { launch?: string; sso_entry?: string };
  auth?: GeneratedAuth;
}

function originBase(origin: string): string {
  return origin.replace(/\/$/, '');
}

function nextcloudEntry(origin: string, integration: PublicIntegrationSnapshot): string {
  const override = integration.nextcloudUserOidcLoginPath;
  if (override) {
    const base = originBase(origin);
    return `${base}${override.startsWith('/') ? override : `/${override}`}`;
  }
  return resolveServiceUrl('nextcloud', 'sso_entry', origin);
}

function buildPoliciesFromManifest(): Record<string, ServiceAuthPolicy> {
  const items = (generatedRegistry.entries ?? []) as GeneratedRedirectItem[];
  const out: Record<string, ServiceAuthPolicy> = {};
  for (const item of items) {
    const auth = item.auth;
    const id = item.id;
    if (!auth || !id) continue;
    const launchPath =
      auth.launchPath ||
      item.paths?.launch ||
      item.entryPath ||
      tryResolveServicePath(id, 'launch') ||
      '/';
    const fallback =
      auth.fallbackLoginPath ||
      `${resolveServicePath('portal', 'service_login')}?service=${id}`;
    out[id] = {
      id,
      title: item.service,
      launchMode: (auth.launchMode as LaunchMode) || 'sso_passthrough',
      logoutMode: (auth.logoutMode as LogoutMode) || 'shared_fallback',
      supportsSilentSso: Boolean(auth.supportsSilentSso),
      launchPath,
      fallbackLoginPath: fallback,
      resolveEntryUrl: (origin, integration = defaultPublicIntegration) => {
        if (id === 'nextcloud') {
          return nextcloudEntry(origin, integration);
        }
        try {
          return resolveServiceUrl(id, 'sso_entry', origin);
        } catch {
          return tryResolveServicePath(id, 'public')
            ? resolveServiceUrl(id, 'public', origin)
            : null;
        }
      },
    };
  }
  return out;
}

export const SERVICE_AUTH_POLICIES = buildPoliciesFromManifest() as Record<
  string,
  ServiceAuthPolicy
>;

export type LaunchableServiceId = keyof typeof SERVICE_AUTH_POLICIES;

export function isLaunchableServiceId(id: string): id is LaunchableServiceId {
  return Object.prototype.hasOwnProperty.call(SERVICE_AUTH_POLICIES, id);
}

export function getServiceAuthPolicy(id: string): ServiceAuthPolicy | null {
  if (!isLaunchableServiceId(id)) return null;
  return SERVICE_AUTH_POLICIES[id];
}

/** Portal-relative launch href for left-rail / widgets (same origin). */
export function getPortalLaunchHref(serviceId: string): string | null {
  const policy = getServiceAuthPolicy(serviceId);
  return policy?.launchPath ?? null;
}

export function buildServiceLoginPath(
  serviceId: string,
  opts?: { switchUser?: boolean; target?: string }
): string {
  const params = new URLSearchParams();
  params.set('service', serviceId);
  if (opts?.switchUser) params.set('switch', '1');
  if (opts?.target) params.set('target', opts.target);
  return `${resolveServicePath('portal', 'service_login')}?${params.toString()}`;
}

/** sessionStorage key for post-OAuth resume into a service. */
export const POST_AUTH_SERVICE_TARGET_KEY = 'post_auth_service_target';
